import { pageTitle } from '@palaccount/config';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { api } from '@/api';
import { BrandMark } from '@/components/brand-mark';
import { LanguageSwitch } from '@/components/language-switch';
import { PasswordInput } from '@/components/password-input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { serverMessageKey } from '@/lib/session';

/**
 * The activation link's landing page (ADM-05 §7: Invited → Active). The
 * person sets their password; validity is checked by the SERVER when the
 * link is opened, not only when it was sent (BR-PERM-27) — a revoked,
 * expired, or already-used link answers the fixed sentence.
 *
 * They arrive with NO role (R18): after activating, sign-in lands them on
 * the rung-4 floor with the "ask your administrator" explanation.
 */
export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneEmail, setDoneEmail] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('invite.title'));
  }, [t]);

  const submit = async () => {
    setError(null);
    if (password !== confirm) {
      setError(t('invite.errMismatch'));
      return;
    }
    setPending(true);
    try {
      const response = await api.users.acceptInvite({ body: { token, password } });
      if (response.status !== 200) {
        const message =
          typeof response.body === 'object' && response.body !== null && 'message' in response.body
            ? String((response.body as { message: unknown }).message)
            : t('invite.errInvalid');
        const key = serverMessageKey(message);
        setError(key ? t(key) : message);
        return;
      }
      setDoneEmail(response.body.email);
    } catch {
      setError(t('invite.errInvalid'));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-xl border bg-background p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <BrandMark />
          <LanguageSwitch />
        </div>

        {doneEmail ? (
          <div className="grid gap-4">
            <h1 className="text-xl font-bold">{t('invite.doneTitle')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('invite.doneBody', { email: doneEmail })}
            </p>
            <Button onClick={() => void navigate('/sign-in')}>{t('auth.signIn')}</Button>
          </div>
        ) : token === '' ? (
          <div className="grid gap-4">
            <h1 className="text-xl font-bold">{t('invite.title')}</h1>
            <p role="alert" className="text-sm text-destructive">
              {t('invite.errInvalid')}
            </p>
            <Link className="text-sm text-primary underline" to="/sign-in">
              {t('auth.signIn')}
            </Link>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <h1 className="text-xl font-bold">{t('invite.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('invite.sub')}</p>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="invite-password">{t('invite.password')}</Label>
              <PasswordInput
                id="invite-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">{t('auth.passwordHint')}</p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="invite-confirm">{t('invite.confirmPassword')}</Label>
              <PasswordInput
                id="invite-confirm"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" disabled={pending || password.length === 0 || confirm.length === 0}>
              {pending ? <Loader2 aria-hidden className="animate-spin" /> : null}
              {t('invite.activate')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
